import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Send, Bot, User as UserIcon, Sparkles, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem('dhs-triage-session');
  if (!sid) {
    sid = 'triage-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    localStorage.setItem('dhs-triage-session', sid);
  }
  return sid;
};

const Triage = () => {
  const [sessionId] = useState(getSessionId());
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\u2019m Homey, your Domestic Home Services assistant. What\u2019s going on with your appliance or home? Tell me the appliance and what it\u2019s doing.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const scrollRef = useRef(null);
  const { toast } = useToast();
  const nav = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (msgText) => {
    const text = (msgText ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/triage/chat`, { session_id: sessionId, message: text });
      const { reply, summary: s } = res.data;
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
      if (s?.ready) setSummary(s);
    } catch (e) {
      toast({ title: 'Chat error', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    localStorage.removeItem('dhs-triage-session');
    axios.delete(`${API}/triage/session/${sessionId}`).catch(() => {});
    window.location.reload();
  };

  const bookWithSummary = () => {
    if (!summary) return;
    const params = new URLSearchParams({
      appliance: summary.appliance || '',
      description: summary.summary || '',
      urgency: summary.urgency || 'normal',
      type: 'Repair',
    }).toString();
    nav(`/dashboard/new?${params}`);
  };

  const suggestions = [
    'My washing machine won’t drain',
    'Fridge is making a loud humming noise',
    'Boiler pressure is very low',
    'Dishwasher not starting at all',
  ];

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5C39]" /> AI-powered symptom triage
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F0068] tracking-tight">Chat with Homey</h1>
            <p className="mt-2 text-[#1F0068]/70">Describe your issue and we’ll help figure out next steps before booking a repair.</p>
          </div>

          <div className="bg-white rounded-3xl border border-[#1F0068]/5 shadow-lg overflow-hidden flex flex-col" style={{ height: '68vh', minHeight: 500 }}>
            <div className="px-5 py-4 bg-[#1F0068] text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Bot className="w-5 h-5" /></div>
                <div>
                  <div className="font-bold">Homey</div>
                  <div className="text-xs text-white/70 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#00b67a]" /> Online · replies in seconds</div>
                </div>
              </div>
              <Button variant="ghost" onClick={clear} className="text-white/80 hover:bg-white/10 gap-1.5 h-9 rounded-full">
                <RefreshCw className="w-3.5 h-3.5" /> New chat
              </Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FFF6EB]">
              {messages.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
                  {m.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-[#1F0068] text-white flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>
                  )}
                  <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === 'user' ? 'bg-[#FF5C39] text-white rounded-br-sm' : 'bg-white text-[#1F0068] border border-[#1F0068]/5 rounded-bl-sm'}`}>
                    {m.content}
                  </div>
                  {m.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-[#FFEDD6] text-[#1F0068] flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4" /></div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex items-end gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#1F0068] text-white flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>
                  <div className="bg-white border border-[#1F0068]/5 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#1F0068]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#1F0068]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#1F0068]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {summary && (
                <div className="bg-white border-2 border-[#00b67a] rounded-2xl p-5 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#00b67a]/15 text-[#00b67a] flex items-center justify-center">✓</div>
                    <div className="font-bold text-[#1F0068]">Triage complete</div>
                  </div>
                  <dl className="text-sm space-y-1 mb-4">
                    <div className="flex justify-between"><dt className="text-[#1F0068]/60">Appliance</dt><dd className="font-semibold text-[#1F0068]">{summary.appliance}</dd></div>
                    <div className="flex justify-between"><dt className="text-[#1F0068]/60">Issue</dt><dd className="font-semibold text-[#1F0068] text-right ml-4">{summary.summary}</dd></div>
                    <div className="flex justify-between"><dt className="text-[#1F0068]/60">Urgency</dt><dd className={`font-bold uppercase ${summary.urgency === 'high' ? 'text-[#FF5C39]' : 'text-[#1F0068]'}`}>{summary.urgency}</dd></div>
                  </dl>
                  {summary.urgency === 'high' && (
                    <div className="flex items-start gap-2 bg-[#FF5C39]/10 text-[#FF5C39] rounded-xl p-3 text-sm mb-3">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div>This looks urgent. Please book an engineer straight away.</div>
                    </div>
                  )}
                  <Button onClick={bookWithSummary} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                    Book a repair with this info <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>

            {messages.length <= 2 && (
              <div className="px-5 pt-3 flex flex-wrap gap-2 bg-[#FFF6EB]">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs bg-white text-[#1F0068] border border-[#1F0068]/10 hover:border-[#1F0068] rounded-full px-3 py-1.5 font-medium">
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 bg-[#FFF6EB] border-t border-[#1F0068]/5 flex items-end gap-2">
              <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Describe the issue…" className="flex-1 min-h-[48px] max-h-32 rounded-2xl bg-white border-[#1F0068]/10 text-[#1F0068] resize-none" />
              <Button type="submit" disabled={loading || !input.trim()} className="h-12 w-12 shrink-0 rounded-2xl bg-[#FF5C39] hover:bg-[#e0492b] text-white p-0">
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>

          <div className="mt-6 text-center text-xs text-[#1F0068]/60">
            Homey is an AI assistant. For gas or serious electrical issues, always contact a qualified gas or electrical engineer directly.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Triage;
